import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {Plugins, ElementPreset, Component} from '@packages/systems/wfdl';
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import {
  EElement,
  EList,
  ERecord,
  EText,
  EBoolean,
  EEnum,
} from '@packages/systems/wfdl-ast';
import {
  AddPanelLocalesList64Icon,
  IDIcon,
  LocalizationIcon,
  TranslateIcon,
  LocationIcon,
} from '@packages/systems/spring';
import LocalesIcon from './icons/temp-element-locales';
import LocalesListIcon from './icons/temp-element-locales-list';
import LocalesItemIcon from './icons/temp-element-locales-item';
import LocalesBlockIcon from './icons/temp-element-locales-block';
import {operand} from '@packages/systems/plugins/ConditionsOperand';

import {
  Hreflangs,
  LocalesWrapper,
  LocalesEmpty,
  LocalesList,
  LocalesItem,
} from './atoms';

import {
  TagType,
  DisplayNameType,
  DisplayImageIdType,
  SubdirectoryType,
  PageIdType,
  ActiveType,
  tagToText,
  tagToListOfElements,
  displayNameToText,
  displayNameToListOfElements,
  subdirectoryToBasicLink,
  pageIdToBasicLink,
  displayImageIdToImage,
  TLocalesTag,
  TLocalesDisplayName,
  TLocalesDisplayImageId,
  TLocalesActive,
  LanguageCodeType,
  CountryCodeType,
  languageCodeToText,
  languageCodeToListOfElements,
  countryCodeToText,
  countryCodeToListOfElements,
  TLocalesLanguageCode,
  TLocalesCountryCode,
} from './types';
import {
  displayNamePredicates,
  displayImageIdPredicates,
  activePredicates,
} from './predicates';
import {
  tagIsAny,
  tagIsNotAny,
  languageCodeIsAny,
  languageCodeIsNotAny,
  countryCodeIsAny,
  countryCodeIsNotAny,
  countryCodeIsSet,
  countryCodeIsNotSet,
  TagAnyOfList,
  LanguageCodeAnyOfList,
  CountryCodeAnyOfList,
} from './localizationCodePredicates';

const LocalesListAddPanelIcon = wrapAddPanelIcon(AddPanelLocalesList64Icon);

export const plugin = Plugins.PrivilegedPlugin({
  atoms: {
    Hreflangs,
    LocalesWrapper,
    LocalesEmpty,
    LocalesList,
    LocalesItem,
  },
  icons: {
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelLocalesList: LocalesListAddPanelIcon,
    Locales: LocalesIcon,
    LocalesBlock: LocalesBlockIcon,
    LocalesItem: LocalesItemIcon,
    LocalesList: LocalesListIcon,
    ID: IDIcon,
    Localization: LocalizationIcon,
    Translate: TranslateIcon,
    Location: LocationIcon,
  },
  dataTypes: {
    Tag: TagType,
    DisplayName: DisplayNameType,
    DisplayImageId: DisplayImageIdType,
    Subdirectory: SubdirectoryType,
    PageId: PageIdType,
    Active: ActiveType,
    LanguageCode: LanguageCodeType,
    CountryCode: CountryCodeType,
    TagAnyOfList,
    LanguageCodeAnyOfList,
    CountryCodeAnyOfList,
  },
  values: {
    tagToText,
    tagToListOfElements,
    displayNameToText,
    displayNameToListOfElements,
    subdirectoryToBasicLink,
    pageIdToBasicLink,
    displayImageIdToImage,
    languageCodeToText,
    languageCodeToListOfElements,
    countryCodeToText,
    countryCodeToListOfElements,
    CurrentActiveLocaleTagCondition: operand(TLocalesTag),
    TagIsAny: tagIsAny,
    TagIsNotAny: tagIsNotAny,
    CurrentActiveLocaleDisplayNameCondition: operand(TLocalesDisplayName),
    ...displayNamePredicates,
    CurrentActiveLocaleDisplayImageIdCondition: operand(TLocalesDisplayImageId),
    ...displayImageIdPredicates,
    CurrentActiveLocaleLanguageCodeCondition: operand(TLocalesLanguageCode),
    LanguageCodeIsAny: languageCodeIsAny,
    LanguageCodeIsNotAny: languageCodeIsNotAny,
    CurrentActiveLocaleCountryCodeCondition: operand(TLocalesCountryCode),
    CountryCodeIsSet: countryCodeIsSet,
    CountryCodeIsNotSet: countryCodeIsNotSet,
    CountryCodeIsAny: countryCodeIsAny,
    CountryCodeIsNotAny: countryCodeIsNotAny,
    LocalesListItemActiveCondition: operand(TLocalesActive),
    ...activePredicates,
  },
  elementPresets: {
    LocalesWrapper: ElementPreset.create({
      label: 'Locales List',
      category: 'Other',
      addTabIndex: 5,
      icon: {
        small: qualify(Plugins.name('Localization'), iconName('LocalesList')),
        medium: qualify(
          Plugins.name('Localization'),
          iconName('AddPanelLocalesList')
        ),
      },
      helpData: {
        description: [
          'A Locales List allows visitors to switch between different locales on your site.',
        ],
      },
      factory: EElement({
        id: 'Locales Wrapper',
        type: qualify(
          Plugins.name('Localization'),
          Component.name('LocalesWrapper')
        ),
        data: ERecord({
          children: EList([
            EElement({
              id: 'Locales List',
              type: qualify(
                Plugins.name('Localization'),
                Component.name('LocalesList')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Locales Item',
                    type: qualify(
                      Plugins.name('Localization'),
                      Component.name('LocalesItem')
                    ),
                    data: ERecord({
                      children: EList([]),
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Locales Empty',
              type: qualify(
                Plugins.name('Localization'),
                Component.name('LocalesEmpty')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Locales Empty Content',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Block')
                    ),
                    data: ERecord({
                      tag: EEnum('div'),
                      text: EBoolean(true),
                      children: EList([
                        EElement({
                          id: 'Locales Empty String',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('No enabled locales found.'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
    }),
  },
});
